import { newId } from "../lib/ids";
import { cleanText, cleanValue, sanitizeUrl } from "../lib/sanitize";
import type {
    ActivityType,
    FinalReason,
    MediaDesc,
    PlaybackEndReason,
    VirtualPlayback,
} from "./types";

const PROGRESS_MS = 60_000;
const LIVENESS_MS = 5_000;
const PAUSE_LEAVE_MS = 10 * 60_000;
const NAVIGATED_WINDOW_MS = 6_000;
const VIRTUAL_PAUSE_MS = 3_000;
const MAX_INTERVALS = 50;
const MAX_SEEKS = 20;

const r1 = (n: number): number => Math.round(n * 10) / 10;
const finite = (n: number | null | undefined): number | undefined =>
    typeof n === "number" && Number.isFinite(n) ? n : undefined;

type Interval = [number, number];

export const mergeIntervals = (list: Interval[]): Interval[] => {
    const sorted = list.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
    const out: Interval[] = [];
    for (const [a, b] of sorted) {
        const last = out[out.length - 1];
        if (last && a <= last[1]) last[1] = Math.max(last[1], b);
        else out.push([a, b]);
    }
    return out;
};

/** Больше 50 отрезков — сливаем соседей с самым маленьким зазором. */
export const compressIntervals = (list: Interval[], max: number = MAX_INTERVALS): Interval[] => {
    const out = list.map(([a, b]): Interval => [a, b]);
    while (out.length > max) {
        let best = 0;
        for (let i = 1; i < out.length - 1; i++) {
            if (out[i + 1][0] - out[i][1] < out[best + 1][0] - out[best][1]) best = i;
        }
        out.splice(best, 2, [out[best][0], out[best + 1][1]]);
    }
    return out;
};

/**
 * Один просмотр одного плеера. Считает не время на странице, а отрезки, которые
 * реально проиграны (в секундах ролика): перемотка вперёд их не прибавляет,
 * пересмотр одного места не удваивает.
 */
export class PlaybackSession {
    readonly id = newId();
    readonly startS: number;
    lastPos: number;
    maxPos: number;
    rate = 1;
    pauses = 0;
    seekCount = 0;
    readonly seekList: Interval[] = [];
    bufferingMs = 0;
    waitingSince: number | null = null;
    pausedAt: number | null = null;
    durationS: number | undefined;
    srcKey = "";
    ended = false;
    private openStart: number | null = null;
    private openMax = 0;
    private readonly closed: Interval[] = [];

    constructor(
        readonly media: MediaDesc,
        readonly ctx: Record<string, unknown> | undefined,
        startPos: number,
        readonly t0: number,
    ) {
        const p = finite(startPos) ?? 0;
        this.startS = p;
        this.lastPos = p;
        this.maxPos = p;
    }

    get isOpen(): boolean {
        return this.openStart !== null;
    }

    open(pos: number, now: number): void {
        if (this.ended) return;
        if (this.waitingSince !== null) {
            this.bufferingMs += Math.max(0, now - this.waitingSince);
            this.waitingSince = null;
        }
        this.pausedAt = null;
        const p = finite(pos) ?? this.lastPos;
        if (this.openStart === null) {
            this.openStart = p;
            this.openMax = p;
        }
        this.advance(p);
    }

    advance(pos: number): void {
        const p = finite(pos);
        if (p === undefined) return;
        this.lastPos = p;
        if (p > this.maxPos) this.maxPos = p;
        if (this.openStart !== null && p > this.openMax) this.openMax = p;
    }

    close(pos?: number): void {
        if (this.openStart === null) return;
        const end = Math.max(this.openMax, finite(pos) ?? -Infinity);
        if (end > this.openStart) this.closed.push([this.openStart, end]);
        this.openStart = null;
    }

    pause(pos: number, now: number): void {
        this.close(pos);
        this.advance(pos);
        if (this.pausedAt === null) this.pauses++;
        this.pausedAt = now;
    }

    waiting(now: number): void {
        this.close();
        if (this.waitingSince === null) this.waitingSince = now;
    }

    seek(from: number, to: number): void {
        this.close();
        this.seekCount++;
        if (this.seekList.length < MAX_SEEKS) this.seekList.push([r1(from), r1(to)]);
        this.advance(to);
    }

    intervals(): Interval[] {
        const all = this.closed.slice();
        if (this.openStart !== null) all.push([this.openStart, this.openMax]);
        return mergeIntervals(all);
    }

    watchedS(): number {
        return this.intervals().reduce((sum, [a, b]) => sum + (b - a), 0);
    }

    progress(): Record<string, unknown> {
        return {
            playback_id: this.id,
            pos_s: r1(this.lastPos),
            watched_s: r1(this.watchedS()),
            max_s: r1(this.maxPos),
        };
    }

    end(reason: PlaybackEndReason, now: number, pos?: number): Record<string, unknown> {
        this.close(pos);
        this.advance(pos ?? NaN);
        if (this.waitingSince !== null) {
            this.bufferingMs += Math.max(0, now - this.waitingSince);
            this.waitingSince = null;
        }
        this.ended = true;
        const merged = this.intervals();
        return {
            playback_id: this.id,
            media: this.media,
            ctx: this.ctx,
            start_s: r1(this.startS),
            end_s: r1(this.lastPos),
            max_s: r1(this.maxPos),
            watched_s: r1(merged.reduce((s, [a, b]) => s + (b - a), 0)),
            wall_s: r1((now - this.t0) / 1000),
            intervals: compressIntervals(merged).map(([a, b]) => [r1(a), r1(b)]),
            seeks: this.seekCount,
            seek_list: this.seekList,
            pauses: this.pauses,
            rate: this.rate,
            buffering_ms: Math.round(this.bufferingMs),
            duration_s: this.durationS === undefined ? undefined : r1(this.durationS),
            end_reason: reason,
        };
    }
}

export interface MediaApi {
    track: (type: ActivityType, data: Record<string, unknown>) => void;
    contexts: () => Record<string, unknown> | undefined;
    lastRouteChangeMs: () => number;
}

const MEDIA_EVENTS = [
    "play",
    "playing",
    "pause",
    "seeking",
    "seeked",
    "ended",
    "ratechange",
    "waiting",
    "emptied",
    "loadstart",
    "timeupdate",
    "error",
];

const MEDIA_HOST =
    "[data-track-media-kind],[data-track-media-id],[data-track-media-title],[data-track-media-src]";

const strip = (o: MediaDesc): MediaDesc => {
    const out: MediaDesc = {};
    (Object.keys(o) as Array<keyof MediaDesc>).forEach((k) => {
        if (o[k]) out[k] = o[k];
    });
    return out;
};

const basename = (url: string): string => {
    try {
        const p = new URL(url).pathname;
        return cleanValue(decodeURIComponent(p.slice(p.lastIndexOf("/") + 1)), 120);
    } catch {
        return "";
    }
};

export const resolveMedia = (el: HTMLMediaElement): MediaDesc => {
    const host = el.closest(MEDIA_HOST);
    const tag = el.tagName.toLowerCase();
    const kind = host?.getAttribute("data-track-media-kind") || undefined;
    if (el.closest("[data-track-private]")) return { kind: kind || tag };
    if (host) {
        const src = host.getAttribute("data-track-media-src");
        return strip({
            kind,
            id: cleanValue(host.getAttribute("data-track-media-id"), 120),
            title: cleanText(host.getAttribute("data-track-media-title"), 120),
            src: src ? sanitizeUrl(src) : undefined,
            tag,
        });
    }
    // blob: у hls.js ничего не говорит и живёт одну страницу — не пишем.
    const cur = el.currentSrc || el.getAttribute("src") || "";
    const http = /^https?:/i.test(cur);
    return strip({
        tag,
        file: http ? basename(cur) : undefined,
        src: http ? sanitizeUrl(cur) : undefined,
    });
};

export const isIgnoredMedia = (el: HTMLMediaElement): boolean =>
    // WebRTC (аватар HeyGen, эфир) и декоративные петли без звука — не просмотр.
    !!(el as HTMLMediaElement & { srcObject?: unknown }).srcObject ||
    (el.muted && el.loop) ||
    !!el.closest("[data-track-ignore]");

const currentTime = (el: HTMLMediaElement): number => finite(el.currentTime) ?? 0;

/** Все <video>/<audio> страницы через перехват в фазе захвата на document. */
export class MediaCapture {
    private readonly byEl = new WeakMap<HTMLMediaElement, PlaybackSession>();
    private readonly active = new Map<PlaybackSession, HTMLMediaElement | null>();
    private progressTimer: ReturnType<typeof setInterval> | null = null;
    private liveTimer: ReturnType<typeof setInterval> | null = null;

    constructor(
        private readonly api: MediaApi,
        private readonly now: () => number = Date.now,
    ) {}

    install(): () => void {
        const opts: AddEventListenerOptions = { capture: true, passive: true };
        MEDIA_EVENTS.forEach((t) => document.addEventListener(t, this.onEvent, opts));
        return () => {
            MEDIA_EVENTS.forEach((t) => document.removeEventListener(t, this.onEvent, opts));
            this.stopTimers();
        };
    }

    onFinal = (why: FinalReason): void => {
        if (why === "pagehide") this.endAll("tab_closed");
        else if (why === "rotate") this.endAll("session_rotate");
    };

    sessionOf(el: HTMLMediaElement): PlaybackSession | undefined {
        return this.byEl.get(el);
    }

    private onEvent = (e: Event): void => {
        const el = e.target;
        if (!(el instanceof HTMLMediaElement)) return;
        try {
            this.handle(e.type, el);
        } catch {
            /* учёт не должен ломать плеер */
        }
    };

    handle(type: string, el: HTMLMediaElement): void {
        let s = this.byEl.get(el);
        const pos = currentTime(el);
        const now = this.now();
        switch (type) {
            case "play":
            case "playing":
                if (isIgnoredMedia(el)) return;
                if (!s) s = this.begin(el, pos, now);
                if (type === "playing") s.open(pos, now);
                break;
            case "pause":
                s?.pause(pos, now);
                break;
            case "waiting":
                s?.waiting(now);
                break;
            case "seeking":
                s?.seek(s.lastPos, pos);
                break;
            case "seeked":
                if (s && !el.paused) s.open(pos, now);
                break;
            case "timeupdate":
                if (!s) break;
                if (!el.paused && !el.seeking && !s.isOpen && s.waitingSince === null) {
                    s.open(pos, now);
                } else s.advance(pos);
                break;
            case "ratechange":
                if (s) s.rate = el.playbackRate || 1;
                break;
            case "ended":
                if (s) this.finish(s, "ended", finite(el.duration) ?? pos);
                break;
            case "emptied":
            case "loadstart":
                if (s && (el.currentSrc || el.getAttribute("src") || "") !== s.srcKey) {
                    this.finish(s, "source_change");
                }
                break;
            case "error":
                if (s) this.finish(s, "error");
                break;
            default:
                break;
        }
        this.ensureTimers();
    }

    private begin(el: HTMLMediaElement, pos: number, now: number): PlaybackSession {
        const s = new PlaybackSession(resolveMedia(el), this.api.contexts(), pos, now);
        s.srcKey = el.currentSrc || el.getAttribute("src") || "";
        s.durationS = finite(el.duration);
        s.rate = el.playbackRate || 1;
        this.byEl.set(el, s);
        this.active.set(s, el);
        this.emitStart(s);
        return s;
    }

    private emitStart(s: PlaybackSession): void {
        this.api.track("play_start", {
            playback_id: s.id,
            media: s.media,
            ctx: s.ctx,
            start_s: r1(s.startS),
            duration_s: s.durationS === undefined ? undefined : r1(s.durationS),
            rate: s.rate,
        });
    }

    private finish(s: PlaybackSession, reason: PlaybackEndReason, pos?: number): void {
        if (s.ended) return;
        const el = this.active.get(s);
        this.active.delete(s);
        if (el) this.byEl.delete(el);
        if (el && s.durationS === undefined) s.durationS = finite(el.duration);
        this.api.track("play_end", s.end(reason, this.now(), pos));
        this.ensureTimers();
    }

    endAll(reason: PlaybackEndReason): void {
        Array.from(this.active.keys()).forEach((s) => this.finish(s, reason));
    }

    endWithin(root: Element | string, reason: PlaybackEndReason): void {
        const node = typeof root === "string" ? document.querySelector(root) : root;
        if (!node) return;
        Array.from(this.active.entries()).forEach(([s, el]) => {
            if (el && node.contains(el)) this.finish(s, reason);
        });
    }

    /**
     * Плеер в iframe (Kinescope): событий DOM нет, есть только позиция. Время,
     * которое идёт вперёд, — это просмотр; скачок больше 2 с × скорость — перемотка;
     * 3 с без обновлений — пауза.
     */
    virtual(desc: MediaDesc): VirtualPlayback {
        let s: PlaybackSession | null = null;
        let last: { pos: number; at: number } | null = null;
        let rate = 1;
        let pauseTimer: ReturnType<typeof setTimeout> | null = null;
        const media = strip({
            ...desc,
            id: desc.id ? cleanValue(desc.id, 120) : undefined,
            title: desc.title ? cleanText(desc.title, 120) : undefined,
        });

        const clearPause = () => {
            if (pauseTimer) clearTimeout(pauseTimer);
            pauseTimer = null;
        };
        const armPause = () => {
            clearPause();
            pauseTimer = setTimeout(() => {
                pauseTimer = null;
                if (s && !s.ended && s.isOpen) s.pause(s.lastPos, this.now());
                this.ensureTimers();
            }, VIRTUAL_PAUSE_MS);
        };
        const ensure = (pos: number): PlaybackSession => {
            if (!s || s.ended) {
                s = new PlaybackSession(media, this.api.contexts(), pos, this.now());
                s.rate = rate;
                this.active.set(s, null);
                this.emitStart(s);
            }
            return s;
        };
        const live = (): PlaybackSession | null => (s && !s.ended ? s : null);

        return {
            play: (pos) => {
                const now = this.now();
                ensure(pos).open(pos, now);
                last = { pos, at: now };
                armPause();
                this.ensureTimers();
            },
            pause: (pos) => {
                clearPause();
                live()?.pause(pos, this.now());
                last = { pos, at: this.now() };
                this.ensureTimers();
            },
            time: (pos) => {
                const p = finite(pos);
                if (p === undefined) return;
                const now = this.now();
                const prev = last;
                last = { pos: p, at: now };
                if (!prev) return;
                const cur = live();
                const open = !!cur && cur.isOpen;
                const expected = open ? ((now - prev.at) / 1000) * rate : 0;
                const delta = p - prev.pos;
                if (Math.abs(delta - expected) > 2 * rate) {
                    cur?.seek(prev.pos, p);
                    armPause();
                    this.ensureTimers();
                    return;
                }
                if (delta > 0) {
                    const ss = ensure(prev.pos);
                    if (!ss.isOpen) ss.open(prev.pos, now);
                    ss.advance(p);
                    armPause();
                    this.ensureTimers();
                }
            },
            seeked: (pos) => {
                const p = finite(pos);
                if (p === undefined) return;
                const cur = live();
                if (cur && Math.abs(p - cur.lastPos) > 0.5) cur.seek(cur.lastPos, p);
                last = { pos: p, at: this.now() };
            },
            rate: (r) => {
                if (finite(r) && r > 0) rate = r;
                const cur = live();
                if (cur) cur.rate = rate;
            },
            waiting: () => {
                live()?.waiting(this.now());
            },
            ended: (pos) => {
                clearPause();
                const cur = live();
                if (cur) this.finish(cur, "ended", pos);
            },
            end: (reason) => {
                clearPause();
                const cur = live();
                if (cur) this.finish(cur, reason);
            },
        };
    }

    private ensureTimers(): void {
        let anyOpen = false;
        this.active.forEach((_, s) => {
            if (s.isOpen) anyOpen = true;
        });
        if (anyOpen && !this.progressTimer) {
            this.progressTimer = setInterval(this.progressTick, PROGRESS_MS);
        } else if (!anyOpen && this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
        if (this.active.size > 0 && !this.liveTimer) {
            this.liveTimer = setInterval(this.liveTick, LIVENESS_MS);
        } else if (this.active.size === 0 && this.liveTimer) {
            clearInterval(this.liveTimer);
            this.liveTimer = null;
        }
    }

    private stopTimers(): void {
        if (this.progressTimer) clearInterval(this.progressTimer);
        if (this.liveTimer) clearInterval(this.liveTimer);
        this.progressTimer = null;
        this.liveTimer = null;
    }

    // Контрольная точка раз в минуту ещё и держит сеанс журнала живым: длинная
    // лекция не должна считаться простоем.
    private progressTick = (): void => {
        this.active.forEach((_, s) => {
            if (s.isOpen) this.api.track("play_progress", s.progress());
        });
        this.ensureTimers();
    };

    liveTick = (): void => {
        const now = this.now();
        Array.from(this.active.entries()).forEach(([s, el]) => {
            if (el && !el.isConnected) {
                const navigated = now - this.api.lastRouteChangeMs() <= NAVIGATED_WINDOW_MS;
                this.finish(s, navigated ? "navigated" : "unmount");
                return;
            }
            if (!s.isOpen && s.pausedAt !== null && now - s.pausedAt > PAUSE_LEAVE_MS) {
                this.finish(s, document.hidden ? "tab_hidden" : "pause_leave");
            }
        });
        this.ensureTimers();
    };
}
